import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wrench, MapPin, Crosshair, ArrowRight, ShieldCheck, History, BusFront } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, fmtTime, todayStr } from "@/lib/format";

const REASONS = [["breakdown", "Breakdown"], ["accident", "Accident"], ["mechanical", "Mechanical fault"], ["fuel", "Out of fuel"], ["tyre", "Tyre / puncture"], ["overheating", "Overheating"], ["driver", "Driver issue"], ["other", "Other"]];
const REASON_LABEL = Object.fromEntries(REASONS);
const ACTIVE = ["scheduled", "boarding", "departed"];
const blank = { new_bus_id: "", reason: "", location: "", lat: null, lng: null, notes: "", new_driver_id: "" };

function SwapDialog({ trip, buses, drivers, otherTrips, onClose, onDone }) {
  const busyBuses = new Set((otherTrips || []).filter((t) => t.id !== trip.id && t.departure === trip.departure && t.bus_id).map((t) => t.bus_id));
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  const useGps = () => {
    if (!navigator.geolocation) return toast.error("Location not available on this device");
    navigator.geolocation.getCurrentPosition(
      (pos) => { const { latitude, longitude } = pos.coords; set({ lat: latitude, lng: longitude, location: form.location || `GPS ${latitude.toFixed(5)}, ${longitude.toFixed(5)}` }); toast.success("Location captured"); },
      () => toast.error("Could not get your location"),
    );
  };

  const submit = async () => {
    if (!form.new_bus_id) return toast.error("Choose a replacement bus");
    if (!form.reason) return toast.error("Choose a reason for the swap");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/trips/${trip.id}/swap-bus`, { ...form, new_driver_id: form.new_driver_id || null });
      toast.success(`Bus swapped${data.insurance_reissued ? " · insurance re-issued" : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-lg" data-testid="swap-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Swap bus · {trip.origin_city} → {trip.destination_city}</DialogTitle>
          <DialogDescription>{fmtDateTime(trip.departure)} · currently bus <b>{trip.bus_id ? trip.bus_number : "unassigned"}</b> · {trip.booked || 0} passenger(s)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Replacement bus *</Label>
            <Select value={form.new_bus_id} onValueChange={(v) => set({ new_bus_id: v })}>
              <SelectTrigger data-testid="swap-bus-select"><SelectValue placeholder="Choose a bus" /></SelectTrigger>
              <SelectContent>{buses.filter((b) => b.id !== trip.bus_id).map((b) => <SelectItem key={b.id} value={b.id} disabled={busyBuses.has(b.id)}>{b.number} · {b.bus_type || ""} · {b.capacity} seats{busyBuses.has(b.id) ? " · busy" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Reason *</Label>
            <Select value={form.reason} onValueChange={(v) => set({ reason: v })}>
              <SelectTrigger data-testid="swap-reason-select"><SelectValue placeholder="Why is the bus being swapped?" /></SelectTrigger>
              <SelectContent>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Breakdown location</Label>
            <div className="flex gap-2">
              <Input className="h-11" placeholder="e.g. Nsawam, Accra–Kumasi road" value={form.location} onChange={(e) => set({ location: e.target.value })} data-testid="swap-location-input" />
              <Button type="button" variant="outline" className="h-11 shrink-0 rounded-xl" onClick={useGps} data-testid="swap-gps-button"><Crosshair className="h-4 w-4" />GPS</Button>
            </div>
            {form.lat != null && <p className="text-xs text-emerald-600">Coordinates captured: {form.lat.toFixed(5)}, {form.lng.toFixed(5)}</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Change driver too (optional)</Label>
            <Select value={form.new_driver_id || "keep"} onValueChange={(v) => set({ new_driver_id: v === "keep" ? "" : v })}>
              <SelectTrigger data-testid="swap-driver-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="keep">Keep current driver</SelectItem>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Notes (for the record / insurance)</Label>
            <Textarea rows={2} placeholder="What happened, any injuries, condition of the old bus…" value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="swap-notes-input" />
          </div>
          <Button disabled={busy} onClick={submit} className="h-12 w-full rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC] btn-press" data-testid="swap-submit-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirm bus swap"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BusSwaps() {
  const [date, setDate] = useState(todayStr());
  const [trips, setTrips] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, bu, dr, sw] = await Promise.all([
        api.get("/admin/trips", { params: { date } }),
        api.get("/admin/buses"),
        api.get("/admin/drivers"),
        api.get("/admin/bus-swaps"),
      ]);
      setTrips(tr.data.filter((t) => ACTIVE.includes(t.status)));
      setBuses(bu.data.filter((b) => b.active !== false));
      setDrivers(dr.data.filter((d) => d.active !== false));
      setSwaps(sw.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="bus-swaps-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-extrabold tracking-tight text-[#182678]"><Wrench className="h-6 w-6 text-[#0052FF]" />Bus breakdowns &amp; swaps</h1>
          <p className="text-sm text-slate-500">Swap a broken-down bus mid-journey. Every swap is logged with reason &amp; location for insurance.</p>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] font-semibold text-slate-500">Trips for</Label>
          <Input type="date" className="h-10 w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="swaps-date" />
        </div>
      </div>

      {loading ? (
        <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>
      ) : (
        <>
          <div className="card-soft p-5" data-testid="swap-active-trips">
            <div className="mb-3 flex items-center gap-2 font-display text-base font-bold text-[#182678]"><BusFront className="h-4 w-4 text-[#0052FF]" />Active trips</div>
            <div className="divide-y divide-slate-100">
              {trips.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`swap-trip-${t.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800">{t.origin_city} → {t.destination_city}</div>
                    <div className="text-xs text-slate-500">{fmtTime(t.departure)} · Bus {t.bus_number} · {t.driver_name || "No driver"} · <span className="uppercase">{t.status}</span></div>
                  </div>
                  <Button size="sm" className="rounded-full bg-amber-500 font-bold text-white hover:bg-amber-600 btn-press" onClick={() => setActive(t)} data-testid={`swap-open-${t.id}`}><Wrench className="h-4 w-4" />Swap bus</Button>
                </div>
              ))}
              {!trips.length && <p className="py-6 text-center text-sm text-slate-400">No active trips on this date.</p>}
            </div>
          </div>

          <div className="card-soft p-5" data-testid="swap-history">
            <div className="mb-3 flex items-center gap-2 font-display text-base font-bold text-[#182678]"><History className="h-4 w-4 text-[#0052FF]" />Swap history</div>
            <div className="-mx-2 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-400">
                  <tr><th className="p-2">When</th><th className="p-2">Route</th><th className="p-2">Bus change</th><th className="p-2">Reason</th><th className="p-2">Location</th><th className="p-2 text-center">Pax</th><th className="p-2">By</th><th className="p-2">Insurance</th></tr>
                </thead>
                <tbody>
                  {swaps.map((s, i) => (
                    <tr key={s.id} className="border-b border-slate-50" data-testid={`swap-row-${i}`}>
                      <td className="whitespace-nowrap p-2 text-slate-500">{fmtDateTime(s.created_at)}</td>
                      <td className="p-2">{s.route}</td>
                      <td className="p-2"><span className="flex items-center gap-1 font-medium"><span className="text-rose-600">{s.old_bus_number || "—"}</span><ArrowRight className="h-3 w-3 text-slate-400" /><span className="text-emerald-700">{s.new_bus_number}</span></span></td>
                      <td className="p-2"><span className="chip bg-amber-100 text-amber-700">{REASON_LABEL[s.reason] || s.reason}</span></td>
                      <td className="p-2 max-w-[180px] truncate text-slate-500" title={s.location}>{s.location || "—"}</td>
                      <td className="p-2 text-center">{s.passengers}</td>
                      <td className="p-2 text-slate-500">{s.performed_by}</td>
                      <td className="p-2">{s.insurance_reissued ? <span className="inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" />Re-issued</span> : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                  {!swaps.length && <tr><td colSpan={8} className="p-6 text-center text-slate-400">No bus swaps recorded yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {active && <SwapDialog trip={active} buses={buses} drivers={drivers} otherTrips={trips} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
}
